import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ROLE_LABEL } from "@/lib/pgt";
import { Loader2, Plus, UserCog, KeyRound, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function Users() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const isManager = me.role === "manager";
  const [addOpen, setAddOpen] = useState(false);
  const [pwUser, setPwUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [delUser, setDelUser] = useState(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["users"],
    queryFn: async () => (await api.get("/users")).data,
  });
  const refresh = () => qc.invalidateQueries({ queryKey: ["users"] });

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { active: !u.active });
      toast.success(u.active ? "User deactivated" : "User activated");
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const changeRole = async (u, role) => {
    try {
      await api.patch(`/users/${u.id}`, { role });
      toast.success("Role updated");
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const changeGender = async (u, gender) => {
    try {
      await api.patch(`/users/${u.id}`, { gender });
      toast.success("Gender updated");
      refresh();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up">
      <div className="mb-5 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-stone-900">User management</h1>
          <p className="mt-1 text-sm text-stone-500">Create and manage sales users.</p>
        </div>
        {me.role === "admin" && (
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={() => setAddOpen(true)} data-testid="add-user-button"><Plus className="mr-1.5 h-4 w-4" /> Add user</Button>
        )}
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-forest" /></div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {users.map((u) => {
            const locked = isManager && u.role === "admin";   // managers can't touch admins
            const isSelf = u.id === me.id;
            return (
            <div key={u.id} className="rounded-xl border border-stone-200 bg-white p-4" data-testid={`user-card-${u.id}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2.5">
                  <span className="grid h-9 w-9 place-items-center rounded-full bg-forest text-ivory text-xs font-bold">{u.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}</span>
                  <div>
                    <div className="font-heading font-semibold text-stone-900">{u.name}</div>
                    <div className="text-xs text-stone-500">{u.email}</div>
                    {u.phone && <div className="text-[11px] text-stone-400">{u.phone}</div>}
                  </div>
                </div>
                <Badge variant="outline" className={u.active ? "text-emerald-700 bg-emerald-50 border-emerald-200 text-[10px]" : "text-stone-500 text-[10px]"}>{u.active ? "Active" : "Inactive"}</Badge>
              </div>
              <div className="mt-3 flex items-center gap-2">
                <Select value={u.role} onValueChange={(v) => changeRole(u, v)} disabled={locked}>
                  <SelectTrigger className="h-8 flex-1 text-xs" data-testid={`user-role-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {!isManager && <SelectItem value="admin">{ROLE_LABEL.admin}</SelectItem>}
                    <SelectItem value="manager">{ROLE_LABEL.manager}</SelectItem>
                    <SelectItem value="executive">{ROLE_LABEL.executive}</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={u.gender || "female"} onValueChange={(v) => changeGender(u, v)} disabled={locked}>
                  <SelectTrigger className="h-8 w-24 text-xs" data-testid={`user-gender-${u.id}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="male">Male</SelectItem>
                  </SelectContent>
                </Select>
                <Switch checked={u.active} onCheckedChange={() => toggleActive(u)} disabled={locked || isSelf} data-testid={`user-active-${u.id}`} />
              </div>
              <div className="mt-2 flex items-center gap-2">
                <Button size="sm" variant="outline" className="h-8 flex-1 text-xs" onClick={() => setEditUser(u)} disabled={locked} data-testid={`user-edit-${u.id}`}><Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit</Button>
                <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setPwUser(u)} disabled={locked} data-testid={`user-pw-${u.id}`}><KeyRound className="h-3.5 w-3.5" /></Button>
                <Button size="sm" variant="outline" className="h-8 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200" onClick={() => setDelUser(u)} disabled={locked || isSelf} data-testid={`user-delete-${u.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
              </div>
            </div>
            );
          })}
        </div>
      )}

      <AddUserDialog open={addOpen} onOpenChange={setAddOpen} onSaved={refresh} />
      {pwUser && <PasswordDialog user={pwUser} onClose={() => setPwUser(null)} />}
      {editUser && <EditUserDialog user={editUser} onClose={() => setEditUser(null)} onSaved={refresh} />}
      {delUser && <DeleteUserDialog user={delUser} onClose={() => setDelUser(null)} onDeleted={refresh} />}
    </div>
  );
}

function AddUserDialog({ open, onOpenChange, onSaved }) {
  const empty = { name: "", email: "", password: "", role: "executive", phone: "", gender: "female" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.email || form.password.length < 6) { toast.error("Name, email and 6+ char password required"); return; }
    setLoading(true);
    try {
      await api.post("/users", form);
      toast.success("User created");
      onSaved(); onOpenChange(false); setForm(empty);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><UserCog className="h-5 w-5" /> Add user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="user-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="user-email-input" /></div>
          <div className="space-y-1.5"><Label>Contact number</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" data-testid="user-phone-input" /></div>
          <div className="space-y-1.5"><Label>Password</Label><Input type="text" value={form.password} onChange={(e) => set("password", e.target.value)} placeholder="min 6 characters" data-testid="user-password-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5"><Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => set("role", v)}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="executive">Sales Executive</SelectItem><SelectItem value="manager">Sales Manager</SelectItem><SelectItem value="admin">Administrator</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Gender</Label>
              <Select value={form.gender} onValueChange={(v) => set("gender", v)}>
                <SelectTrigger data-testid="user-gender-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="female">Female</SelectItem><SelectItem value="male">Male</SelectItem></SelectContent>
              </Select>
              <p className="text-[10px] text-stone-400">Sets Hindi/Hinglish grammar</p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={loading} data-testid="user-save">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PasswordDialog({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);
  const save = async () => {
    if (pw.length < 6) { toast.error("Password must be 6+ characters"); return; }
    setLoading(true);
    try { await api.patch(`/users/${user.id}`, { password: pw }); toast.success("Password reset"); onClose(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle className="font-heading">Reset password · {user.name}</DialogTitle></DialogHeader>
        <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="New password" data-testid="reset-password-input" />
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={loading} data-testid="reset-password-submit">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Reset"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function EditUserDialog({ user, onClose, onSaved }) {
  const [form, setForm] = useState({ name: user.name || "", email: user.email || "", phone: user.phone || "" });
  const [loading, setLoading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setLoading(true);
    try {
      await api.patch(`/users/${user.id}`, { name: form.name, email: form.email, phone: form.phone });
      toast.success("User updated");
      onSaved(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Pencil className="h-4 w-4" /> Edit user</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="edit-user-name-input" /></div>
          <div className="space-y-1.5"><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="edit-user-email-input" /></div>
          <div className="space-y-1.5"><Label>Contact number</Label><Input value={form.phone} onChange={(e) => set("phone", e.target.value)} placeholder="+91 98765 43210" data-testid="edit-user-phone-input" /></div>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-forest hover:bg-forest-600 text-ivory" onClick={save} disabled={loading} data-testid="edit-user-save">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save changes"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialog({ user, onClose, onDeleted }) {
  const [loading, setLoading] = useState(false);
  const remove = async () => {
    setLoading(true);
    try {
      await api.delete(`/users/${user.id}`);
      toast.success(`Removed ${user.name}`);
      onDeleted(); onClose();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="delete-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-red-700">Remove user</DialogTitle>
          <DialogDescription>
            This permanently removes <span className="font-semibold text-stone-700">{user.name}</span> ({user.email}). Their account will no longer be able to sign in. If they still have assigned customers, reassign or archive those first. Consider deactivating instead of removing to keep history.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={remove} disabled={loading} data-testid="delete-user-confirm">{loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Trash2 className="mr-1.5 h-4 w-4" /> Remove user</>}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
